/**
 * Loans settings (configuration.settings.list → /loans/configuration/settings). LOP-owned.
 *
 * Bill 2026-11-03: every loans setting in one list, with who changed it last. What can be changed is the API's decision per setting
 * (the page only mirrors it): credentials are never shown ("(set)" / "(not set)") and never changed here; settings with their own
 * page or owned by the cutover are read-only with the reason beside them; a setting with a fixed list of values offers only those
 * (the API refuses anything else); a value that is JSON today must still be valid JSON.
 *
 * Bill 2026-11-03, on his phone: "needs plain language description and guidance for each setting". Every setting now carries its
 * plain-language entry from the API (help: label, group, what it does, allowed values, default, guidance, related page):
 *   - grouped by the friendly group, in a fixed reading order; the search matches labels, descriptions and guidance as well as keys;
 *   - each row leads with the plain label, the raw key small and muted under it, and the one-line "what it does";
 *   - every row opens the panel (a full-screen sheet on a phone): the full description, the allowed values with what each means,
 *     the guidance and a link to the page that manages it, then the edit fields when the setting can be changed here;
 *   - settings the API reads that have no value stored yet are listed too, marked "not set" (they use the built-in default).
 *
 * Layout follows the house editor pattern (src/modules/System/SettingsPage.tsx): a Card with counts in the title, grouped DataTables
 * (stacked cards under 640px), every edit in a ModalShell carrying the change reason, the saving state and the error; a dismissible
 * toast on success.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { SelectField, TextArea, TextField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';
import { ReasonField, asciiReason, reasonProblem } from '@/components/ChangeReason';
import { loansApi } from '../loansApi';
import { errorText, fmtCt } from '../writeups/common';
import { modalError } from '../adminKit';

interface SettingOption {
  value: string;
  meaning: string;
}

interface SettingHelp {
  label: string;
  group: string;
  what: string;
  values: string;
  options: SettingOption[] | null;
  defaultValue: string | null;
  guidance: string;
  relatedLabel: string | null;
  relatedPath: string | null;
  status: 'described' | 'undocumented' | 'leftover';
  readByApi: boolean;
}

interface LoansSetting {
  key: string;
  area: string;
  value: string | null;
  isSet: boolean;
  /** False for a setting the API reads that has no stored value yet (the built-in default applies). */
  hasRow?: boolean;
  isActive: boolean;
  isJson: boolean;
  notes: string | null;
  updatedBy: string | null;
  updatedUtc: string | null;
  editable: boolean;
  masked: boolean;
  readOnlyNote: string | null;
  choices: string[] | null;
  /** Absent only from an older API build; the page then falls back to the key. */
  help?: SettingHelp | null;
}

interface Draft {
  row: LoansSetting;
  value: string;
  notes: string;
}

const PREVIEW = 48;

/** Reading order of the groups (the API's catalog order); any group not listed sorts after these, "Other" last. */
const GROUP_ORDER = [
  'Write-ups',
  'Write-up cutover',
  'Write-up e-mails',
  'Approvals',
  'Adverse action',
  'Post-closing tasks (Planner)',
  'Encompass post-closing',
  'Encompass pipeline preview',
  'Encompass connection',
  'Boarding',
  'Boarding: Encompass loan list',
  'Support reports',
  'KPI dashboard',
  'Sign-in security',
];

function groupRank(g: string): number {
  if (g === 'Other') return 10_000;
  const i = GROUP_ORDER.indexOf(g);
  return i < 0 ? 5_000 : i;
}

const labelOf = (r: LoansSetting): string => r.help?.label || r.key;
const groupOf = (r: LoansSetting): string => r.help?.group || r.area;
const hasRow = (r: LoansSetting): boolean => r.hasRow !== false;

function prettyJson(v: string): string {
  try {
    return JSON.stringify(JSON.parse(v), null, 2);
  } catch {
    return v;
  }
}

function valueCell(r: LoansSetting): JSX.Element {
  if (!hasRow(r)) {
    return (
      <span className="cell-desc">
        <Pill tone="muted">not set</Pill>
        {r.help?.defaultValue ? ` default ${r.help.defaultValue}` : ''}
      </span>
    );
  }
  if (r.masked) return r.isSet ? <Pill tone="info">(set)</Pill> : <Pill tone="muted">(not set)</Pill>;
  if (r.value === null || r.value.trim() === '') return <span className="cell-dim">(blank)</span>;
  const v = r.value.length > PREVIEW ? `${r.value.slice(0, PREVIEW)}…` : r.value;
  return <span className={r.isJson ? 'cell-mono' : 'cell-mono--bold'} style={{ wordBreak: 'break-word' }}>{v}</span>;
}

function statusPills(r: LoansSetting): JSX.Element | null {
  const pills: JSX.Element[] = [];
  if (r.help?.status === 'leftover') pills.push(<Pill key="l" tone="muted">not used</Pill>);
  if (r.help?.status === 'undocumented' || !r.help) pills.push(<Pill key="u" tone="warn">not documented</Pill>);
  if (hasRow(r) && !r.isActive) pills.push(<Pill key="o" tone="muted">switched off</Pill>);
  if (!r.editable && hasRow(r)) pills.push(<Pill key="r" tone="muted">read-only</Pill>);
  if (pills.length === 0) return null;
  return <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>{pills}</span>;
}

const preStyle = {
  maxHeight: 260,
  overflow: 'auto',
  fontSize: 12,
  background: 'var(--surface-2, #f6f7f9)',
  padding: 12,
  borderRadius: 6,
  margin: 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
} as const;

const keyStyle = { display: 'block', fontSize: 11, color: 'var(--ink-600)', wordBreak: 'break-all', marginTop: 2 } as const;
const whatStyle = { display: 'block', fontSize: 12, color: 'var(--ink-600)', marginTop: 4, lineHeight: 1.4 } as const;
const sectionStyle = { margin: '0 0 var(--sp-7, 14px)' } as const;
const sectionHead = {
  display: 'block',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: 'var(--ink-600)',
  fontWeight: 500,
  marginBottom: 4,
} as const;
const proseStyle = { margin: 0, lineHeight: 1.5, overflowWrap: 'anywhere' } as const;

/** The help half of the panel: what it does, values, default, guidance, related page. */
function HelpBlock({ r }: { r: LoansSetting }): JSX.Element {
  const h = r.help;
  if (!h) {
    return (
      <Banner tone="warn">
        This API build sends no description for settings. Purpose not documented — ask LOP before changing.
      </Banner>
    );
  }
  return (
    <div>
      <div style={sectionStyle}>
        <span style={sectionHead}>What it does</span>
        <p style={proseStyle}>{h.what}</p>
      </div>
      <div style={sectionStyle}>
        <span style={sectionHead}>Allowed values</span>
        {h.options && h.options.length > 0 ? (
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
            {h.options.map((o) => (
              <li key={o.value}>
                <code className="cell-mono--bold">{o.value}</code> — {o.meaning}
              </li>
            ))}
          </ul>
        ) : (
          <p style={proseStyle}>{h.values}</p>
        )}
        {h.options && h.options.length > 0 && h.values && <p style={{ ...proseStyle, marginTop: 4 }} className="cell-desc">{h.values}</p>}
        {h.defaultValue && (
          <p style={{ ...proseStyle, marginTop: 4 }} className="cell-desc">
            Built-in default (used when not set): <code className="cell-mono">{h.defaultValue}</code>
          </p>
        )}
      </div>
      <div style={sectionStyle}>
        <span style={sectionHead}>Guidance</span>
        <p style={proseStyle}>{h.guidance}</p>
      </div>
      {h.relatedLabel && (
        <div style={sectionStyle}>
          <span style={sectionHead}>Managed on</span>
          {h.relatedPath ? <Link to={h.relatedPath}>{h.relatedLabel}</Link> : <span>{h.relatedLabel}</span>}
        </div>
      )}
    </div>
  );
}

export function LoansSettingsPage(): JSX.Element {
  const [rows, setRows] = useState<LoansSetting[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [filter, setFilter] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoadError(null);
      try {
        const r = await loansApi.get<{ settings: LoansSetting[] }>('v3/loans/admin/settings');
        if (!cancelled) setRows(r.settings ?? []);
      } catch (e) {
        if (!cancelled) setLoadError(errorText(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const words = filter.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return rows;
    return rows.filter((r) => {
      const h = r.help;
      const hay = [
        r.key,
        r.masked ? '' : r.value ?? '',
        r.notes ?? '',
        r.updatedBy ?? '',
        r.readOnlyNote ?? '',
        h?.label ?? '',
        h?.group ?? '',
        h?.what ?? '',
        h?.values ?? '',
        h?.guidance ?? '',
        (h?.options ?? []).map((o) => `${o.value} ${o.meaning}`).join(' '),
      ]
        .join(' ')
        .toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [rows, filter]);

  const grouped = useMemo(() => {
    const m = new Map<string, LoansSetting[]>();
    for (const r of filtered) m.set(groupOf(r), [...(m.get(groupOf(r)) ?? []), r]);
    return [...m.entries()].sort(([a], [b]) => groupRank(a) - groupRank(b) || a.localeCompare(b));
  }, [filtered]);

  const openRow = (r: LoansSetting): void => {
    setReason('');
    setTried(false);
    setSaveError(null);
    setDraft({ row: r, value: r.isJson && r.value ? prettyJson(r.value) : r.value ?? '', notes: r.notes ?? '' });
  };

  const closeModal = (): void => {
    if (saving) return;
    setDraft(null);
    setSaveError(null);
  };

  const problem = ((): string | null => {
    if (!draft || !draft.row.editable) return null;
    const { row } = draft;
    if (row.choices && !row.choices.some((c) => c.toLowerCase() === draft.value.trim().toLowerCase())) {
      return `Choose one of: ${row.choices.join(', ')}.`;
    }
    if (row.isJson) {
      try {
        JSON.parse(draft.value);
      } catch {
        return 'The value must be valid JSON, like the current one.';
      }
    }
    if (draft.notes.trim().length > 400) return 'Keep the notes to 400 characters.';
    const sameValue = row.isJson && row.value
      ? prettyJson(row.value) === prettyJson(draft.value)
      : (row.value ?? '').trim().toLowerCase() === draft.value.trim().toLowerCase() &&
        (row.choices ? true : (row.value ?? '').trim() === draft.value.trim());
    if (sameValue && (row.notes ?? '') === draft.notes.trim()) return 'Nothing has changed.';
    return null;
  })();

  const save = async (): Promise<void> => {
    if (!draft) return;
    setTried(true);
    if (problem) {
      setSaveError(problem);
      return;
    }
    if (reasonProblem(reason, { asciiOnly: true })) return;
    setSaving(true);
    setSaveError(null);
    try {
      const value = draft.row.isJson ? JSON.stringify(JSON.parse(draft.value)) : draft.value.trim();
      await loansApi.send('PUT', 'v3/loans/admin/settings', { key: draft.row.key, value, notes: draft.notes.trim() }, asciiReason(reason));
      setToast(`Saved "${labelOf(draft.row)}".`);
      setDraft(null);
      refresh();
    } catch (e) {
      setSaveError(modalError(e));
    } finally {
      setSaving(false);
    }
  };

  const columns: ReadonlyArray<DataTableColumn<LoansSetting>> = [
    {
      header: 'Setting',
      cardTitle: true,
      cell: (r) => (
        <span style={{ display: 'block', minWidth: 0 }}>
          <span className="cell-strong" style={{ overflowWrap: 'anywhere' }}>{labelOf(r)}</span>
          {r.help?.label && <code style={keyStyle}>{r.key}</code>}
          {r.help?.what && <span style={whatStyle}>{r.help.what}</span>}
        </span>
      ),
      sortKey: (r) => labelOf(r),
    },
    {
      header: 'Value',
      width: '22%',
      cell: (r) => (
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
          {valueCell(r)}
          {statusPills(r)}
        </span>
      ),
    },
    {
      header: 'Updated',
      width: '18%',
      hideInCard: true,   // on a phone the sheet shows who changed it and when
      cell: (r) =>
        hasRow(r) ? (
          <span className="cell-desc">
            {fmtCt(r.updatedUtc)}
            {r.updatedBy ? ` by ${r.updatedBy}` : ''}
          </span>
        ) : (
          <span className="cell-dim">—</span>
        ),
      sortKey: (r) => r.updatedUtc ?? '',
    },
    {
      header: '',
      width: 90,
      cell: (r) => (
        <Button
          variant={r.editable ? 'primary' : 'ghost'}
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            openRow(r);
          }}
        >
          {r.editable ? 'Edit' : 'Details'}
        </Button>
      ),
    },
  ];

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load the loans settings: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading the loans settings…" />;

  const editableCount = rows.filter((r) => r.editable).length;
  const notSetCount = rows.filter((r) => !hasRow(r)).length;
  const row = draft?.row;
  const editing = !!row?.editable;
  const longOrJson = draft ? draft.row.isJson || draft.value.length > 80 || draft.value.includes('\n') : false;
  const optionLabel = (v: string): string => {
    const m = row?.help?.options?.find((o) => o.value.toLowerCase() === v.toLowerCase())?.meaning;
    return m ? `${v} — ${m}` : v;
  };
  const selectValue = row?.choices?.find((c) => c.toLowerCase() === draft?.value.trim().toLowerCase()) ?? '';

  return (
    <div>
      <h2>Loans settings</h2>
      <p className="page-intro">
        Every setting the loans API reads, in plain words, grouped by what it affects. Tap a setting for what it does, the values it
        takes and what to check before changing it. {editableCount} of {rows.length} can be changed here; the rest say why not.
        {notSetCount > 0 && ` ${notSetCount} have no value stored and use their built-in default.`} Credentials are never shown. A
        change takes effect the next time the API reads the setting (within a few minutes).
      </p>

      {toast && (
        <Banner tone="info">
          {toast} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card title={filter.trim() ? `Loans settings — ${filtered.length} of ${rows.length}` : `Loans settings — ${rows.length}`}>
        <div style={{ width: '100%', maxWidth: 560 }}>
          <TextField
            label="Search"
            value={filter}
            onChange={setFilter}
            placeholder="e.g. Encompass, e-mail, boarding, store…"
            hint="Matches the plain names, descriptions and guidance as well as the setting keys and values."
          />
        </div>
        {grouped.length === 0 && <p className="empty">{rows.length ? 'No settings match the search.' : 'There are no settings.'}</p>}
        {grouped.map(([group, list]) => (
          <section key={group} className="settings-category">
            <h3 className="settings-category__heading">
              {group}
              <span className="settings-category__count">({list.length})</span>
            </h3>
            <DataTable<LoansSetting>
              columns={columns}
              rows={list}
              rowKey={(r) => r.key}
              onRowClick={openRow}
              selectedKey={draft?.row.key ?? null}
              caption={`${group} settings`}
            />
          </section>
        ))}
      </Card>

      <ModalShell
        open={draft !== null}
        title={row ? labelOf(row) : ''}
        onClose={closeModal}
        width={680}
        actions={
          draft &&
          (editing ? (
            <>
              <Button onClick={closeModal} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void save()} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </>
          ) : (
            <Button onClick={closeModal}>Close</Button>
          ))
        }
      >
        {draft && row && (
          <div className="form-stack">
            <code style={{ ...keyStyle, marginTop: 0, marginBottom: 12 }}>{row.key}</code>
            {saveError && <Banner tone="err">{saveError}</Banner>}
            {row.help?.status === 'leftover' && (
              <Banner tone="info">Not read by the current API (left over). Changing it changes nothing.</Banner>
            )}
            {row.help?.status === 'undocumented' && (
              <Banner tone="warn">Purpose not documented — ask LOP before changing.</Banner>
            )}
            {!hasRow(row) && (
              <Banner tone="info">
                Not set: the API uses its built-in default
                {row.help?.defaultValue ? ` (${row.help.defaultValue})` : ''}. It cannot be added from this page.
              </Banner>
            )}
            {hasRow(row) && !row.isActive && (
              <Banner tone="warn">This setting is switched off, so the loans API does not read it. Saving does not switch it on.</Banner>
            )}
            {!editing && hasRow(row) && row.readOnlyNote && <Banner tone="info">Read-only here: {row.readOnlyNote}.</Banner>}

            <HelpBlock r={row} />

            {hasRow(row) && (
              <div className="form-field">
                <span className="form-field__label">Current value</span>
                {row.masked ? (
                  <span>{row.isSet ? '(set — hidden)' : '(not set)'}</span>
                ) : row.isJson && row.value ? (
                  <pre style={preStyle}>{prettyJson(row.value)}</pre>
                ) : (
                  <code className="cell-mono--bold" style={{ wordBreak: 'break-word' }}>
                    {row.value && row.value.trim() !== '' ? row.value : '(blank)'}
                  </code>
                )}
                {row.updatedUtc && (
                  <span className="cell-desc" style={{ display: 'block', marginTop: 4 }}>
                    Changed {fmtCt(row.updatedUtc)}
                    {row.updatedBy ? ` by ${row.updatedBy}` : ''}
                  </span>
                )}
                {!editing && row.notes && (
                  <span className="cell-desc" style={{ display: 'block', marginTop: 4 }}>
                    Notes: {row.notes}
                  </span>
                )}
              </div>
            )}

            {editing && (
              <>
                {row.choices ? (
                  <SelectField
                    label="New value"
                    value={selectValue}
                    onChange={(v) => setDraft({ ...draft, value: v })}
                    placeholder={selectValue ? undefined : 'Choose a value…'}
                    options={row.choices.map((c) => ({ value: c, label: optionLabel(c) }))}
                    hint="Only these values are allowed for this setting."
                  />
                ) : longOrJson ? (
                  <TextArea
                    label={row.isJson ? 'New value (JSON)' : 'New value'}
                    value={draft.value}
                    onChange={(v) => setDraft({ ...draft, value: v })}
                    rows={row.isJson ? 12 : 4}
                    hint={row.isJson ? 'Must be valid JSON. It is checked before it is saved.' : row.help?.values}
                    error={tried && row.isJson && problem?.includes('JSON') ? problem : undefined}
                  />
                ) : (
                  <TextField
                    label="New value"
                    value={draft.value}
                    onChange={(v) => setDraft({ ...draft, value: v })}
                    hint={row.help?.values}
                  />
                )}

                <TextArea
                  label="Notes"
                  value={draft.notes}
                  onChange={(v) => setDraft({ ...draft, notes: v })}
                  rows={2}
                  hint="Why it is set this way. 400 characters at most."
                />
                <ReasonField value={reason} onChange={setReason} showError={tried} asciiOnly />
              </>
            )}
          </div>
        )}
      </ModalShell>
    </div>
  );
}
